import React, { useState, useEffect } from "react";
import GoToTop from "../GoToTop";
import { useParams } from "react-router-dom";
import { supabase } from "../supabase";
import { formatINR } from "../utils/currency";
import fuel from "./assets/images/fuel.webp";
import seat from "./assets/images/seat.webp";
import Loader from "./Loader";
import driver from "./assets/images/driver.png";

function OfflineBookings() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const { data, error } = await supabase
          .from("OfflineBookings")
          .select(
            `
              *,
              Vehicles (*), Drivers(*)   
            `,
          )
          .eq("id", id)
          .single();

        if (error) {
          console.error("Error fetching booking:", error);
        } else {
          setBooking(data);
        }
      } catch (err) {
        console.error("Unexpected error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBooking();
    }
  }, [id]);

  const formatTimestampTime = (value) => {
    if (!value) return "N/A";

    const date = new Date(value);

    return date.toLocaleString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTimestampDate = (value) => {
    if (!value) return "N/A";

    const date = new Date(value);

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDuration = (minutes) => {
    if (minutes === null || minutes === undefined) return "0 mins";

    const totalMinutes = Math.round(Number(minutes));

    if (isNaN(totalMinutes)) return "0 mins";

    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;

    if (hrs > 0 && mins > 0) return `${hrs} hr ${mins} mins`;
    if (hrs > 0) return `${hrs} hr`;
    return `${mins} mins`;
  };

  return (
    <>
      <GoToTop />
      <div className="container web-main" style={{ marginTop: "90px" }}>
        <h6 style={{ color: "grey" }}>
          Booking Details
          {/* <small style={{ textTransform: "lowercase" }}>
                      {booking?.pickup}-to-{booking?.drop}
                    </small> */}
        </h6>

        {loading && <Loader />}

        {!loading && booking && (
          <>
            <div className="uber-summary-card">
              <div className="uber-locations">
                <div className="location-row">
                  <div className="dot pickup-dot"></div>
                  <div className="location-text">
                    <span className="label">Pickup</span>
                    <span className="value">{booking?.pickup}</span>
                  </div>
                </div>

                <div className="short-vertical-line"></div>

                <div className="location-row">
                  <div className="square drop-square"></div>
                  <div className="location-text">
                    <span className="label">Drop</span>
                    <span className="value">{booking?.drop}</span>
                  </div>
                </div>
              </div>

              <div className="uber-meta">
                {booking?.isScheduled ? (
                  <span className="meta-badge">
                    Scheduled • {formatTimestampDate(booking?.pickuptime)},{" "}
                    {formatTimestampTime(booking?.pickuptime)}
                  </span>
                ) : (
                  <>
                    <span className="meta-badge">Instant pickup</span>{" "}
                    <span className="meta-badge bg-dark text-light">
                      Pick & Drop
                    </span>
                  </>
                )}

                {booking?.distance && booking?.duration && (
                  <span className="meta-time">
                    {booking?.distance} km • {formatDuration(booking?.duration)}
                  </span>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center p-3 rounded-4 mb-2 border uber-ride-card mt-2">
              <div className="d-flex align-items-center gap-3">
                <img
                  src={booking?.Vehicles?.img_url}
                  className="img-fluid"
                  style={{ width: "85px", mixBlendMode: "darken" }}
                />

                <div>
                  <h6 className="mb-0">
                    <span style={{ textTransform: "Capitalize" }}>
                      {booking.veh_color}
                    </span>{" "}
                    {booking?.Vehicles?.name}{" "}
                  </h6>
                  <small
                    className="mb-0"
                    style={{ fontSize: "12px", marginTop: "-5px" }}
                  >
                    {booking?.veh_number}
                  </small>
                </div>
              </div>

              <div style={{ textAlign: "left" }}>
                <h6 className="mb-0" style={{ fontSize: "13px" }}>
                  <img src={fuel} className="icon-img" />
                  {booking?.Vehicles?.fuel}
                </h6>
                <h6
                  className="mb-0"
                  style={{ fontSize: "13px", marginTop: "5px" }}
                >
                  <img src={seat} className="icon-img" />
                  {booking?.Vehicles?.seat} seater
                </h6>
              </div>
            </div>

            <div className="d-flex align-items-center p-3 rounded-4 mb-2 border uber-ride-card mt-2">
              {/* Left Section */}
              <div className="d-flex gap-3 align-items-center">
                <img
                  src={booking?.Drivers?.profile_img || driver}
                  className="img-fluid"
                  style={{
                    width: "50px",
                    height: "50px",
                    objectFit: "cover",
                    borderRadius: "20%",
                  }}
                  alt="driver"
                />

                <div>
                  <small
                    className="mb-0"
                    style={{ fontSize: "12px", color: "grey" }}
                  >
                    #DGDR-2026-0{booking?.Drivers?.id}
                  </small>
                  <h6 className="mb-1">{booking?.Drivers?.name}</h6>
                </div>
              </div>

              {/* Right Section */}
              {booking?.Drivers?.rating && (
                <div className="ms-auto text-end">
                  <small style={{ margin: 0 }}>
                    ⭐ <b>{booking?.Drivers?.rating}</b>
                    <span style={{ color: "grey" }}>/5</span>
                  </small>
                </div>
              )}
            </div>

            <div className="p-3 rounded-4 mb-2 border mt-2">
              <div className="row">
                <div className="col-8">
                  <small style={{ fontSize: "14px" }}>Ride Amount :</small>
                </div>
                <div style={{ textAlign: "right" }} className="col-4">
                  <b style={{ fontSize: "14px" }}>
                    {formatINR(booking.amount)}
                  </b>
                </div>

                <div className="col-8">
                  <small style={{ fontSize: "14px" }}>Waiting Charge :</small>
                </div>
                <div style={{ textAlign: "right" }} className="col-4">
                  <b style={{ fontSize: "14px" }}>
                    {formatINR(booking.extra_amount)}
                  </b>
                </div>

                <hr className="mb-2 mt-2" />

                <div className="col-6">
                  <small style={{ fontSize: "15px" }}>Grand Total :</small>
                </div>
                <div style={{ textAlign: "right" }} className="col-6">
                  <b style={{ fontSize: "17px", color: "green" }}>
                    {formatINR(Number(booking.amount) + Number(booking.extra_amount))}
                  </b>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default OfflineBookings;
